import React, { useState, useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import FormInput from '../../components/FormInput';
import Toggle from 'react-toggle';
import Toast from '../../components/Toast';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import CaretIcon from '../../icons/rev_carat.png';
import HeaderIconSvg from '../../icons/newicons/arrow-down-up.svg';
import PencilIcon from '../../icons/pencil.svg';
import TickIcon from '../../icons/tick.svg';
import CrossIcon from '../../icons/cross.svg';

import {
  PRICE_MODIFIER_URL,
  UPDATE_HEDGE_DATA_URL,
  UPDATE_PIECE_COUNT_URL,
  RESET_PIECE_COUNT_URL,
  GET_ENTITY_DETAIL_URL,
  getData,
  putData,
} from '../../utils/api';
import { getLoginDetails } from '../../utils/storage';

const HedgeManagement = (props) => {
  const hedgeAccessEmails = [
    'admin@abcattech.com',
    'brad@abcattech.com',
    'grader1@abcattech.com',
    'chris@abcattech.com',
  ];
  const loginDetails = getLoginDetails() || {};
  const canAccessHedge = hedgeAccessEmails.includes(
    (loginDetails.email || '').toLowerCase()
  );
  const { companyId } = props.match.params;
  const companyName = props.location?.state?.companyName || '';
  const weightedAverageFromState = props.location?.state?.weightedAverage || '';
  const customCountFromState = props.location?.state?.customCount;
  const customPtFromState = props.location?.state?.customPt;
  const customPdFromState = props.location?.state?.customPd;
  const customRhFromState = props.location?.state?.customRh;
  const isApplyCustomHedgeFromState = props.location?.state?.isApplyCustomHedge;
  const [hedgeData, setHedgeData] = useState({
    hedge: { pt: '', pd: '', rh: '' },
    discount: { pt: '', pd: '', rh: '' },
    netPriceApplied: { pt: '', pd: '', rh: '' },
  });
  const [customValues, setCustomValues] = useState({
    pt: '',
    pd: '',
    rh: '',
  });
  const [percentageValues, setPercentageValues] = useState({
    pt: '',
    pd: '',
    rh: '',
  });
  const [applyCustomHedge, setApplyCustomHedge] = useState(false);
  const [pieceCount, setPieceCount] = useState('');
  const [isPieceCountEditing, setIsPieceCountEditing] = useState(false);
  const [pieceCountTemp, setPieceCountTemp] = useState('');
  const [itemsUtilised, setItemsUtilised] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [customNetPriceApplied, setCustomNetPriceApplied] = useState({
    pt: '',
    pd: '',
    rh: '',
  });
  const [weightedAverage, setWeightedAverage] = useState('');
  const [toastParams, setToastParams] = useState({
    message: '',
    type: '',
    id: '',
  });
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState(null);
  const [showResetConfirmPopup, setShowResetConfirmPopup] = useState(false);

  useEffect(() => {
    if (!canAccessHedge) {
      props.history.replace('/entities/viewGroup');
    }
  }, [canAccessHedge, props.history]);

  useEffect(() => {
    if (!canAccessHedge) return;
    if (companyId) {
      fetchHedgeData();
      fetchWeightedAverage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessHedge, companyId]);

  // Poll for itemsUtilised updates every 5 seconds
  useEffect(() => {
    if (!canAccessHedge) return;
    if (!companyId) return;

    const pollItemsUtilised = async () => {
      try {
        const entityData = await getData(GET_ENTITY_DETAIL_URL, {
          companyId: companyId,
        });
        if (entityData && entityData.itemsUtilised !== undefined && entityData.itemsUtilised !== null) {
          setItemsUtilised(entityData.itemsUtilised.toString());
        }
      } catch (e) {
        console.log('Error polling itemsUtilised', e);
        // Silently fail - don't show error toast for background polling
      }
    };

    // Poll immediately, then every 5 seconds
    pollItemsUtilised();
    const intervalId = setInterval(pollItemsUtilised, 5000);

    // Cleanup interval on unmount or when companyId changes
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessHedge, companyId]);

  const fetchWeightedAverage = async () => {
    try {
      const entityData = await getData(GET_ENTITY_DETAIL_URL, {
        companyId: companyId,
      });
      if (entityData) {
        // Update weightedAverage
        if (entityData.weightedAverage !== undefined && entityData.weightedAverage !== null) {
          setWeightedAverage(entityData.weightedAverage.toString());
        } else {
          setWeightedAverage('0');
        }
        // Update isApplyCustomHedge
        if (entityData.isApplyCustomHedge !== undefined) {
          setApplyCustomHedge(entityData.isApplyCustomHedge);
        }
        // Update custom values if available
        if (entityData.customPt !== undefined && entityData.customPt !== null) {
          setCustomValues({
            pt: entityData.customPt.toString(),
            pd: entityData.customPd !== undefined && entityData.customPd !== null ? entityData.customPd.toString() : '',
            rh: entityData.customRh !== undefined && entityData.customRh !== null ? entityData.customRh.toString() : '',
          });
        }
        // Update piece count (customCount) from API
        if (entityData.customCount !== undefined && entityData.customCount !== null) {
          setPieceCount(entityData.customCount.toString());
        } else {
          setPieceCount('0');
        }
        // Update items utilised from API
        if (entityData.itemsUtilised !== undefined && entityData.itemsUtilised !== null) {
          setItemsUtilised(entityData.itemsUtilised.toString());
        } else {
          setItemsUtilised('0');
        }
      }
    } catch (e) {
      console.log('Error fetching entity data', e);
    }
  };

  // Set custom values from route state if available (only when navigating from entity list, not on page reload)
  useEffect(() => {
    // Only use route state if we have it AND haven't loaded from API yet
    // The API fetch will override these values on page reload
    if (customPtFromState !== undefined && customPtFromState !== null) {
      setCustomValues({
        pt: customPtFromState.toString(),
        pd: customPdFromState !== undefined && customPdFromState !== null ? customPdFromState.toString() : '',
        rh: customRhFromState !== undefined && customRhFromState !== null ? customRhFromState.toString() : '',
      });
    }
    // Note: customCount and itemsUtilised are always fetched from API, not from route state
    if (isApplyCustomHedgeFromState !== undefined) {
      setApplyCustomHedge(isApplyCustomHedgeFromState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPtFromState, customPdFromState, customRhFromState, isApplyCustomHedgeFromState]);

  // Recalculate custom values when hedge changes and percentage is set
  useEffect(() => {
    ['pt', 'pd', 'rh'].forEach((field) => {
      const percentageValue = percentageValues[field];
      if (percentageValue && hedgeData.hedge[field]) {
        const percentage = parseFloat(percentageValue);
        if (!isNaN(percentage)) {
          const multiplier = percentage >= 0 
            ? (1 + percentage / 100)
            : (1 - Math.abs(percentage) / 100);

          const hedgeValue = hedgeData.hedge[field];
          const customValue = hedgeValue 
            ? (parseFloat(hedgeValue) * multiplier).toFixed(2)
            : '';

          setCustomValues((prev) => {
            const updated = {
              ...prev,
              [field]: customValue,
            };
            
            // Calculate custom net price applied for this field
            const discountValue = hedgeData.discount[field];
            const customNetPrice = calculateCustomNetPriceApplied(customValue, discountValue);
            setCustomNetPriceApplied((prevNet) => ({
              ...prevNet,
              [field]: customNetPrice,
            }));
            
            return updated;
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hedgeData.hedge.pt, hedgeData.hedge.pd, hedgeData.hedge.rh, hedgeData.discount.pt, hedgeData.discount.pd, hedgeData.discount.rh, percentageValues.pt, percentageValues.pd, percentageValues.rh]);

  // Recalculate custom net price applied when custom values or discount changes (only if not set by percentage)
  useEffect(() => {
    ['pt', 'pd', 'rh'].forEach((field) => {
      // Only recalculate if percentage is not set (meaning user directly edited custom value)
      if (!percentageValues[field]) {
        const customValue = customValues[field];
        const discountValue = hedgeData.discount[field];
        const customNetPrice = calculateCustomNetPriceApplied(customValue, discountValue);
        setCustomNetPriceApplied((prev) => ({
          ...prev,
          [field]: customNetPrice,
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customValues.pt, customValues.pd, customValues.rh, hedgeData.discount.pt, hedgeData.discount.pd, hedgeData.discount.rh]);

  const calculateNetPriceApplied = (hedge, discount) => {
    // Calculate net price applied: ptOz - (ptOz*ptDisc/100)
    // discount has negative symbol, so use absolute value
    const pt = hedge.pt && discount.pt 
      ? (parseFloat(hedge.pt) - (parseFloat(hedge.pt) * Math.abs(parseFloat(discount.pt)) / 100)).toFixed(2)
      : '';
    const pd = hedge.pd && discount.pd 
      ? (parseFloat(hedge.pd) - (parseFloat(hedge.pd) * Math.abs(parseFloat(discount.pd)) / 100)).toFixed(2)
      : '';
    const rh = hedge.rh && discount.rh 
      ? (parseFloat(hedge.rh) - (parseFloat(hedge.rh) * Math.abs(parseFloat(discount.rh)) / 100)).toFixed(2)
      : '';
    
    return { pt, pd, rh };
  };

  const fetchHedgeData = async () => {
    try {
      setIsLoading(true);
      const data = await getData(PRICE_MODIFIER_URL, {
        ptDiscIsPercentage: true,
        pdDiscIsPercentage: true,
        rhDiscIsPercentage: true,
      });
      
      // Extract abcPrice object from response
      const abcPrice = data.abcPrice || {};
      
      // Map values from API response
      const hedge = {
        pt: abcPrice.ptOz?.toString() || '',
        pd: abcPrice.pdOz?.toString() || '',
        rh: abcPrice.rhOz?.toString() || '',
      };
      
      const discount = {
        pt: abcPrice.ptDisc?.toString() || '',
        pd: abcPrice.pdDisc?.toString() || '',
        rh: abcPrice.rhDisc?.toString() || '',
      };
      
      // Calculate net price applied
      const netPriceApplied = calculateNetPriceApplied(hedge, discount);
      
      setHedgeData({
        hedge,
        discount,
        netPriceApplied,
      });
      
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to fetch hedge data',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };


  const handleToggleChange = (newValue) => {
    setPendingToggleValue(newValue);
    setShowConfirmPopup(true);
  };

  const handleConfirmToggle = async () => {
    try {
      setIsLoading(true);
      setShowConfirmPopup(false);
      
      // Prepare payload with custom values - convert to proper types
      const payload = {
        companyId: companyId,
        isApplyCustomHedge: pendingToggleValue,
        customPt: customValues.pt ? parseFloat(customValues.pt) : 0.0,
        customPd: customValues.pd ? parseFloat(customValues.pd) : 0.0,
        customRh: customValues.rh ? parseFloat(customValues.rh) : 0.0,
        customCount: pieceCount ? parseInt(pieceCount, 10) : 0,
      };

      const response = await putData(UPDATE_HEDGE_DATA_URL, payload);

      setApplyCustomHedge(pendingToggleValue);
      
      // Update weightedAverage from save API response first, then refetch to ensure latest value
      if (response && response.weightedAverage !== undefined && response.weightedAverage !== null) {
        setWeightedAverage(response.weightedAverage.toString());
      } else {
        setWeightedAverage('0');
      }
      
      // Always refetch from entity detail API after save to get the latest weightedAverage
      fetchWeightedAverage();
      
      setToastParams({
        type: 'SUCCESS',
        message: 'Hedge settings updated successfully',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to update hedge settings',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const handleCancelToggle = () => {
    setShowConfirmPopup(false);
    setPendingToggleValue(null);
  };

  const handleEditPieceCount = () => {
    setPieceCountTemp(pieceCount);
    setIsPieceCountEditing(true);
  };

  const handleCancelPieceCountEdit = () => {
    setPieceCountTemp('');
    setIsPieceCountEditing(false);
  };

  const handleSavePieceCount = async () => {
    try {
      setIsLoading(true);
      
      const payload = {
        companyId: companyId,
        customCount: pieceCountTemp ? parseInt(pieceCountTemp, 10) : 0,
      };

      await putData(UPDATE_PIECE_COUNT_URL, payload);
      
      // Fetch updated entity data to get latest customCount and itemsUtilised
      const entityData = await getData(GET_ENTITY_DETAIL_URL, {
        companyId: companyId,
      });
      
      if (entityData) {
        // Update piece count
        if (entityData.customCount !== undefined && entityData.customCount !== null) {
          setPieceCount(entityData.customCount.toString());
        }
        // Update items utilised
        if (entityData.itemsUtilised !== undefined && entityData.itemsUtilised !== null) {
          setItemsUtilised(entityData.itemsUtilised.toString());
        } else {
          setItemsUtilised('0');
        }
      }
      
      setIsPieceCountEditing(false);
      setPieceCountTemp('');
      
      setToastParams({
        type: 'SUCCESS',
        message: 'Piece count saved successfully',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to save piece count',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const handleResetClick = () => {
    setShowResetConfirmPopup(true);
  };

  const handleConfirmReset = async () => {
    try {
      setIsLoading(true);
      setShowResetConfirmPopup(false);
      
      const payload = {
        companyId: companyId,
        isReset: true,
      };

      await putData(RESET_PIECE_COUNT_URL, payload);
      
      // Fetch updated entity data to get latest customCount and itemsUtilised
      const entityData = await getData(GET_ENTITY_DETAIL_URL, {
        companyId: companyId,
      });
      
      if (entityData) {
        // Update piece count
        if (entityData.customCount !== undefined && entityData.customCount !== null) {
          setPieceCount(entityData.customCount.toString());
        }
        // Update items utilised
        if (entityData.itemsUtilised !== undefined && entityData.itemsUtilised !== null) {
          setItemsUtilised(entityData.itemsUtilised.toString());
        } else {
          setItemsUtilised('0');
        }
      }
      
      setToastParams({
        type: 'SUCCESS',
        message: 'Items Utilised reset successfully',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to reset items utilised',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const handleCancelReset = () => {
    setShowResetConfirmPopup(false);
  };

  const updateHedgeData = (row, field, value) => {
    setHedgeData((prev) => {
      const updated = {
        ...prev,
        [row]: {
          ...prev[row],
          [field]: value === '' ? '' : value,
        },
      };
      
      // Recalculate net price applied when hedge or discount changes
      if (row === 'hedge' || row === 'discount') {
        updated.netPriceApplied = calculateNetPriceApplied(updated.hedge, updated.discount);
      }
      
      return updated;
    });
  };

  const calculateCustomNetPriceApplied = (customValue, discountValue) => {
    if (!customValue || !discountValue) return '';
    // Apply discount to custom value: customValue - (customValue * abs(discount)/100)
    const custom = parseFloat(customValue);
    const discount = parseFloat(discountValue);
    if (isNaN(custom) || isNaN(discount)) return '';
    return (custom - (custom * Math.abs(discount) / 100)).toFixed(2);
  };

  const updateCustomValue = (field, value) => {
    setCustomValues((prev) => {
      const updated = {
        ...prev,
        [field]: value === '' ? '' : value,
      };
      
      // Calculate custom net price applied for this field
      const discountValue = hedgeData.discount[field];
      const customNetPrice = calculateCustomNetPriceApplied(value, discountValue);
      setCustomNetPriceApplied((prevNet) => ({
        ...prevNet,
        [field]: customNetPrice,
      }));
      
      return updated;
    });
    // Clear percentage value for this field when user directly edits custom value
    setPercentageValues((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handlePercentageChange = (field, value) => {
    setPercentageValues((prev) => ({
      ...prev,
      [field]: value === '' ? '' : value,
    }));
    
    if (value === '') {
      // Clear custom value for this field if percentage is cleared
      setCustomValues((prev) => ({
        ...prev,
        [field]: '',
      }));
      return;
    }

    const percentage = parseFloat(value);
    if (isNaN(percentage)) return;

    // Calculate custom value by applying percentage to hedge for this field
    // If positive: hedge * (1 + percentage/100)
    // If negative: hedge * (1 - abs(percentage)/100)
    const multiplier = percentage >= 0 
      ? (1 + percentage / 100)
      : (1 - Math.abs(percentage) / 100);

    const hedgeValue = hedgeData.hedge[field];
    
    const customValue = hedgeValue 
      ? (parseFloat(hedgeValue) * multiplier).toFixed(2)
      : '';

    setCustomValues((prev) => {
      const updated = {
        ...prev,
        [field]: customValue,
      };
      
      // Calculate custom net price applied for this field
      const discountValue = hedgeData.discount[field];
      const customNetPrice = calculateCustomNetPriceApplied(customValue, discountValue);
      setCustomNetPriceApplied((prevNet) => ({
        ...prevNet,
        [field]: customNetPrice,
      }));
      
      return updated;
    });
  };

  return (
    <PageLayout
      icon={HeaderIconSvg}
      heading="Hedge Management"
      searchPlaceholder=""
      hideSearchBar={true}
    >
      <div
        className="flex flex-middle c-pointer"
        onClick={() => {
          props.history.goBack();
        }}
        style={{ marginBottom: '20px' }}
      >
        <img
          src={CaretIcon}
          className="wt-20 rotate-90 op-5 pr-8"
          alt="Back"
        />
        <span className="fs-12 c-table-header fw-bolder">BACK TO CUSTOMERS</span>
      </div>
      {companyName && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#6c757d', marginRight: '8px' }}>
              Customer:
            </span>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#212529' }}>
              {companyName}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#6c757d', marginRight: '8px' }}>
              Weighted Average:
            </span>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#212529' }}>
              {weightedAverage || '0'}
            </span>
          </div>
        </div>
      )}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          marginTop: '20px',
          padding: '16px',
        }}
      >
        {/* Main Table */}
        <div style={{ marginBottom: '20px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e0e0e0',
            }}
          >
            {/* Table Header */}
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#212529',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#212529',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  PT
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#212529',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  PD
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#212529',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  RH
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Hedge Row */}
              <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                <td
                  style={{
                    padding: '14px 16px',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212529',
                    backgroundColor: '#ffffff',
                  }}
                >
                  Hedge
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212529',
                    backgroundColor: '#ffffff',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.hedge.pt || '0'}
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212529',
                    backgroundColor: '#ffffff',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.hedge.pd || '0'}
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212529',
                    backgroundColor: '#ffffff',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.hedge.rh || '0'}
                </td>
              </tr>

              {/* Discount Row */}
              <tr style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#fafbfc' }}>
                <td
                  style={{
                    padding: '14px 16px',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212529',
                    backgroundColor: '#fafbfc',
                  }}
                >
                  Discount
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#dc3545',
                    backgroundColor: '#fafbfc',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.discount.pt || '0'}
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#dc3545',
                    backgroundColor: '#fafbfc',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.discount.pd || '0'}
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#dc3545',
                    backgroundColor: '#fafbfc',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.discount.rh || '0'}
                </td>
              </tr>

              {/* Net Price Applied Row */}
              <tr>
                <td
                  style={{
                    padding: '14px 16px',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212529',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <span>
                    Net Price Applied <span style={{ fontSize: '12px', fontWeight: '400', color: '#6c757d' }}>(without Custom Values)</span>
                  </span>
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '15px',
                    color: '#2e7d32',
                    backgroundColor: '#ffffff',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.netPriceApplied.pt || '0.00'}
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '15px',
                    color: '#2e7d32',
                    backgroundColor: '#ffffff',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.netPriceApplied.pd || '0.00'}
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '15px',
                    color: '#2e7d32',
                    backgroundColor: '#ffffff',
                    fontFamily: 'monospace',
                  }}
                >
                  {hedgeData.netPriceApplied.rh || '0.00'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Custom Values Section */}
        <div
          style={{
            borderTop: '2px solid #e9ecef',
            paddingTop: '16px',
            marginTop: '16px',
          }}
        >
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#212529', marginBottom: '0' }}>
              Custom Values
            </h3>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '20px',
              marginBottom: '16px',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#6c757d', flex: 1 }}>
                  PT % (e.g., 10 or -5)
                </label>
                <FormInput
                  inputClassName="bg-blue rounded-pill"
                  style={{
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    width: '80px',
                    height: '32px',
                    fontSize: '13px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    textAlign: 'center',
                  }}
                  type="number"
                  step="0.01"
                  placeholder=""
                  value={percentageValues.pt === '' ? '' : percentageValues.pt}
                  onChange={(e) => handlePercentageChange('pt', e.target.value)}
                />
              </div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '6px' }}>
                Custom PT
              </label>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  width: '100%',
                  height: '40px',
                  fontSize: '14px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                }}
                type="number"
                step="0.01"
                value={customValues.pt === '' ? '' : customValues.pt}
                onChange={(e) => updateCustomValue('pt', e.target.value)}
              />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#6c757d', flex: 1 }}>
                  PD % (e.g., 10 or -5)
                </label>
                <FormInput
                  inputClassName="bg-blue rounded-pill"
                  style={{
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    width: '80px',
                    height: '32px',
                    fontSize: '13px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    textAlign: 'center',
                  }}
                  type="number"
                  step="0.01"
                  placeholder=""
                  value={percentageValues.pd === '' ? '' : percentageValues.pd}
                  onChange={(e) => handlePercentageChange('pd', e.target.value)}
                />
              </div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '6px' }}>
                Custom PD
              </label>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  width: '100%',
                  height: '40px',
                  fontSize: '14px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                }}
                type="number"
                step="0.01"
                value={customValues.pd === '' ? '' : customValues.pd}
                onChange={(e) => updateCustomValue('pd', e.target.value)}
              />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#6c757d', flex: 1 }}>
                  RH % (e.g., 10 or -5)
                </label>
                <FormInput
                  inputClassName="bg-blue rounded-pill"
                  style={{
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    width: '80px',
                    height: '32px',
                    fontSize: '13px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    textAlign: 'center',
                  }}
                  type="number"
                  step="0.01"
                  placeholder=""
                  value={percentageValues.rh === '' ? '' : percentageValues.rh}
                  onChange={(e) => handlePercentageChange('rh', e.target.value)}
                />
              </div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '6px' }}>
                Custom RH
              </label>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  width: '100%',
                  height: '40px',
                  fontSize: '14px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                }}
                type="number"
                step="0.01"
                value={customValues.rh === '' ? '' : customValues.rh}
                onChange={(e) => updateCustomValue('rh', e.target.value)}
              />
            </div>
          </div>

          {/* Custom Net Price Applied Row */}
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}
          >
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#212529' }}>
                Net Price Applied <span style={{ fontSize: '12px', fontWeight: '400', color: '#6c757d' }}>(with Custom Values after applying discount)</span>
              </label>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '20px',
              }}
            >
              <div>
                <div style={{ 
                  padding: '12px 16px', 
                  fontSize: '15px', 
                  backgroundColor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2e7d32',
                  fontWeight: '700',
                  borderRadius: '6px',
                  border: '1px solid #c8e6c9',
                  fontFamily: 'monospace',
                  minHeight: '44px',
                }}>
                  {customNetPriceApplied.pt || '0.00'}
                </div>
              </div>
              <div>
                <div style={{ 
                  padding: '12px 16px', 
                  fontSize: '15px', 
                  backgroundColor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2e7d32',
                  fontWeight: '700',
                  borderRadius: '6px',
                  border: '1px solid #c8e6c9',
                  fontFamily: 'monospace',
                  minHeight: '44px',
                }}>
                  {customNetPriceApplied.pd || '0.00'}
                </div>
              </div>
              <div>
                <div style={{ 
                  padding: '12px 16px', 
                  fontSize: '15px', 
                  backgroundColor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2e7d32',
                  fontWeight: '700',
                  borderRadius: '6px',
                  border: '1px solid #c8e6c9',
                  fontFamily: 'monospace',
                  minHeight: '44px',
                }}>
                  {customNetPriceApplied.rh || '0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Piece Count, Items Utilised, and Apply Custom Hedge Toggle */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}
          >
            {/* Left Section: Piece Count and Items Utilised */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '32px', flex: 1 }}>
            {/* Piece Count Field with Edit/Save */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#6c757d', whiteSpace: 'nowrap', minWidth: '100px', lineHeight: '20px' }}>
                Piece Count:
              </label>
              {isPieceCountEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FormInput
                      className=""
                      inputClassName="bg-blue rounded-pill"
                      style={{
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        width: '100px',
                        height: '32px',
                        fontSize: '14px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        margin: 0,
                      }}
                      type="number"
                      min="0"
                      step="1"
                      inputPlaceHolder="Enter"
                      value={pieceCountTemp === '' ? '' : pieceCountTemp}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string or valid integers
                        if (value === '') {
                          setPieceCountTemp('');
                        } else {
                          const numValue = parseInt(value, 10);
                          if (!isNaN(numValue) && numValue >= 0) {
                            setPieceCountTemp(value);
                          }
                        }
                      }}
                    />
                    <button
                      onClick={handleSavePieceCount}
                      disabled={isLoading}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: isLoading ? 0.5 : 1,
                      }}
                      title="Save"
                    >
                      <img src={TickIcon} alt="Save" style={{ width: '18px', height: '18px' }} />
                    </button>
                    <button
                      onClick={handleCancelPieceCountEdit}
                      disabled={isLoading}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: isLoading ? 0.5 : 1,
                      }}
                      title="Cancel"
                    >
                      <img src={CrossIcon} alt="Cancel" style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', height: '20px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#212529', minWidth: '40px', textAlign: 'left', lineHeight: '20px', paddingRight: '2px' }}>
                      {pieceCount || '0'}
                    </span>
                    <button
                      onClick={handleEditPieceCount}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        margin: '0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '20px',
                        width: '18px',
                      }}
                      title="Edit"
                    >
                      <img src={PencilIcon} alt="Edit" style={{ width: '14px', height: '14px' }} />
                    </button>
                  </div>
                )}
              </div>

              {/* Items Utilised Field with Reset */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#6c757d', whiteSpace: 'nowrap', minWidth: '110px', lineHeight: '20px' }}>
                  Items Utilised:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', height: '20px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#212529', minWidth: '40px', textAlign: 'left', lineHeight: '20px', paddingRight: '2px' }}>
                    {itemsUtilised || '0'}
                  </span>
                  <button
                    onClick={handleResetClick}
                    disabled={isLoading}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      padding: '0',
                      margin: '0',
                      fontSize: '12px',
                      color: '#dc3545',
                      fontWeight: '500',
                      opacity: isLoading ? 0.5 : 1,
                      whiteSpace: 'nowrap',
                      lineHeight: '20px',
                      height: '20px',
                    }}
                    title="Reset"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Right Section: Apply Custom Hedge Toggle */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Toggle
                className="bg-theme"
                checked={applyCustomHedge}
                onChange={(e) => handleToggleChange(e.target.checked)}
              />
              <label style={{ fontSize: '15px', fontWeight: '500', color: '#212529', cursor: 'pointer', margin: 0, whiteSpace: 'nowrap' }}>
                Apply Custom Hedge
              </label>
            </div>
          </div>
        </div>

      </div>

      <Toast
        type={toastParams.type}
        message={toastParams.message}
        id={toastParams.id}
      />
      {showConfirmPopup && (
        <Modal onClose={handleCancelToggle}>
          <div className="pl-16 pr-16">
            <div className="fs-18 fw-bolder t-center mb-20">
              Are you sure you want to {pendingToggleValue ? 'enable' : 'disable'} custom hedge?
            </div>
            <div className="flex flex-middle flex-center">
              <Button
                className="mr-20 btn ht-40 btn-red"
                onClick={handleCancelToggle}
              >
                Cancel
              </Button>
              <Button
                type="modalSave"
                className="btn ht-40"
                onClick={handleConfirmToggle}
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {showResetConfirmPopup && (
        <Modal onClose={handleCancelReset}>
          <div className="pl-16 pr-16">
            <div className="fs-18 fw-bolder t-center mb-20">
              Are you sure you want to reset Items Utilised?
            </div>
            <div className="flex flex-middle flex-center">
              <Button
                className="mr-20 btn ht-40 btn-red"
                onClick={handleCancelReset}
              >
                Cancel
              </Button>
              <Button
                type="modalSave"
                className="btn ht-40"
                onClick={handleConfirmReset}
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default HedgeManagement;
